import type { TelegramWebApp } from '@/types'
import type { HapticType, PlatformService, SafeAreaInsets } from './types'

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp }
  }
}

const ZERO_INSETS: SafeAreaInsets = { top: 0, bottom: 0, left: 0, right: 0 }

export class TelegramPlatform implements PlatformService {
  readonly mode = 'tg' as const
  private backHandler: (() => void) | null = null
  private mainHandler: (() => void) | null = null

  private get tg(): TelegramWebApp | null {
    return window.Telegram?.WebApp ?? null
  }

  isTelegram() { return true }
  isWeb() { return false }

  async ready() {
    this.tg?.ready()
  }

  goBack() {
    // В TG нативная кнопка уже вызывает обработчик; здесь — программный back
    window.history.back()
  }

  openLink(url: string) {
    const tg = this.tg
    if (tg && typeof (tg as unknown as { openLink?: (u: string) => void }).openLink === 'function') {
      (tg as unknown as { openLink: (u: string) => void }).openLink(url)
    } else {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  haptic(type: HapticType) {
    const hf = this.tg?.HapticFeedback
    if (!hf) return
    if (type === 'success' || type === 'error') {
      hf.notificationOccurred(type)
    } else {
      hf.impactOccurred(type)
    }
  }

  close() {
    this.tg?.close()
  }

  getStartParam() {
    return this.tg?.initDataUnsafe?.start_param
  }

  getInitData() {
    return this.tg?.initData
  }

  expand() {
    this.tg?.expand()
  }

  isExpanded() {
    return Boolean((this.tg as unknown as { isExpanded?: boolean } | null)?.isExpanded)
  }

  requestFullscreen() {
    const tg = this.tg
    if (!tg?.requestFullscreen) return false
    try {
      tg.requestFullscreen()
      return true
    } catch {
      return false
    }
  }

  disableVerticalSwipes() {
    this.tg?.disableVerticalSwipes?.()
  }

  enableVerticalSwipes() {
    this.tg?.enableVerticalSwipes?.()
  }

  setHeaderColor(color: string) {
    const tg = this.tg
    if (!tg) return
    try { tg.headerColor = color } catch { /* noop */ }
  }

  setBottomBarColor(color: string) {
    const tg = this.tg
    if (!tg) return
    try { tg.bottomBarColor = color } catch { /* noop */ }
    try { tg.setBottomBarColor?.(color) } catch { /* noop */ }
  }

  setBackgroundColor(color: string) {
    const tg = this.tg
    if (!tg) return
    try { tg.backgroundColor = color } catch { /* noop */ }
  }

  showBackButton(onClick: () => void) {
    const btn = this.tg?.BackButton
    if (!btn) return
    if (this.backHandler) btn.offClick?.(this.backHandler)
    this.backHandler = onClick
    btn.onClick(onClick)
    btn.show()
  }

  hideBackButton() {
    const btn = this.tg?.BackButton
    if (!btn) return
    if (this.backHandler) {
      btn.offClick?.(this.backHandler)
      this.backHandler = null
    }
    btn.hide()
  }

  showMainButton(text: string, onClick: () => void) {
    const btn = this.tg?.MainButton as (typeof this.tg extends null ? never : { setText: (t: string) => void; onClick: (cb: () => void) => void; offClick?: (cb: () => void) => void; show: () => void; hide: () => void }) | undefined
    if (!btn) return
    btn.setText(text)
    if (this.mainHandler) btn.offClick?.(this.mainHandler)
    this.mainHandler = onClick
    btn.onClick(onClick)
    btn.show()
  }

  hideMainButton() {
    const btn = this.tg?.MainButton as (typeof this.tg extends null ? never : { offClick?: (cb: () => void) => void; hide: () => void }) | undefined
    if (!btn) return
    if (this.mainHandler) {
      btn.offClick?.(this.mainHandler)
      this.mainHandler = null
    }
    btn.hide()
  }

  getSafeAreaInsets(): SafeAreaInsets {
    const tg = this.tg
    if (!tg) return ZERO_INSETS
    const v = tg.safeAreaInset ?? ZERO_INSETS
    const c = tg.contentSafeAreaInset ?? ZERO_INSETS
    return {
      top: v.top + c.top,
      bottom: v.bottom + c.bottom,
      left: v.left + c.left,
      right: v.right + c.right
    }
  }

  applySafeAreaInsetsCssVars() {
    const tg = this.tg
    const root = document.documentElement
    const v = tg?.safeAreaInset ?? ZERO_INSETS
    const c = tg?.contentSafeAreaInset ?? ZERO_INSETS
    root.style.setProperty('--tg-viewport-safe-area-inset-top', `${v.top}px`)
    root.style.setProperty('--tg-viewport-safe-area-inset-bottom', `${v.bottom}px`)
    root.style.setProperty('--tg-viewport-safe-area-inset-left', `${v.left}px`)
    root.style.setProperty('--tg-viewport-safe-area-inset-right', `${v.right}px`)
    root.style.setProperty('--tg-content-safe-area-inset-top', `${c.top}px`)
    root.style.setProperty('--tg-content-safe-area-inset-bottom', `${c.bottom}px`)
    root.style.setProperty('--tg-content-safe-area-inset-left', `${c.left}px`)
    root.style.setProperty('--tg-content-safe-area-inset-right', `${c.right}px`)
  }

  onViewportChanged(cb: () => void) {
    const tg = this.tg as unknown as { onEvent?: (e: string, h: () => void) => void; offEvent?: (e: string, h: () => void) => void } | null
    if (!tg?.onEvent) return () => {}
    try {
      tg.onEvent('viewportChanged', cb)
    } catch { /* noop */ }
    return () => { try { tg.offEvent?.('viewportChanged', cb) } catch { /* noop */ } }
  }

  getTelegramUser() {
    const u = this.tg?.initDataUnsafe?.user
    if (!u) return null
    return {
      id: u.id,
      first_name: u.first_name,
      last_name: u.last_name,
      username: u.username,
      language_code: u.language_code,
      photo_url: u.photo_url
    }
  }
}
